import { useMemo, useState, useEffect } from "react";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { Button } from "@/shared/components/form/Button";
import { InputText } from "@/shared/components/form/InputText";
import { InputSelect } from "@/shared/components/form/InputSelect";
import { useAppShellBreadcrumb } from "@/shared/components/layout/useAppShellBreadcrumb";
import useTranslations from "@/shared/lang/useTranslations";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export const ProfileEditPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t: tGlobal } = useTranslations("global");
  const breadcrumb = useMemo(
    () => [{ label: tGlobal("nav_user_account"), to: "/app/profile/view" }],
    [tGlobal],
  );
  useAppShellBreadcrumb(breadcrumb);

  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone_number: "",
    country: "",
    preferred_language: "",
    timezone: "",
  });

  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        full_name: user.full_name || "",
        email: user.email || "",
        phone_number: user.phone_number || "",
        country: user.country || "",
        preferred_language: user.preferred_language || "",
        timezone: user.timezone || "",
      });
    }
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    try {

      await new Promise((resolve) => setTimeout(resolve, 1000));

      navigate("/app/profile/view");
    } catch (error) {
      console.error("Error updating profile:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const languageOptions = [
    { label: "Español", value: "es" },
    { label: "English", value: "en" },
    { label: "Português", value: "pt" },
  ];

  return (
    <div className="space-y-6 p-2">
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          onClick={() => navigate("/app/profile/view")}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Volver
        </Button>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          Editar Perfil
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Información Personal
          </h2>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                Nombre completo
              </label>
              <InputText
                value={formData.full_name}
                onChange={(e) =>
                  setFormData({ ...formData, full_name: e.target.value })
                }
                placeholder="Nombre completo"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                Email
              </label>
              <InputText
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                placeholder="email@ejemplo.com"
                disabled
                className="bg-gray-50 dark:bg-zinc-800 cursor-not-allowed"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                El email no se puede modificar
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Información de Contacto
          </h2>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                Teléfono
              </label>
              <InputText
                type="tel"
                value={formData.phone_number}
                onChange={(e) =>
                  setFormData({ ...formData, phone_number: e.target.value })
                }
                placeholder="+1 234 567 8900"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                País
              </label>
              <InputText
                value={formData.country}
                onChange={(e) =>
                  setFormData({ ...formData, country: e.target.value })
                }
                placeholder="País"
              />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Preferencias
          </h2>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                Idioma preferido
              </label>
              <InputSelect
                options={languageOptions}
                value={languageOptions.find(
                  (opt) => opt.value === formData.preferred_language
                )}
                onChange={(value) => {
                  if (value && "value" in value) {
                    setFormData({
                      ...formData,
                      preferred_language: value.value,
                    });
                  }
                }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                Zona horaria
              </label>
              <InputText
                value={formData.timezone}
                onChange={(e) =>
                  setFormData({ ...formData, timezone: e.target.value })
                }
                placeholder="America/Mexico_City"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/app/profile/view")}
            disabled={isSaving}
          >
            Cancelar
          </Button>
          <Button type="submit" variant="primary" isLoading={isSaving}>
            Guardar Cambios
          </Button>
        </div>
      </form>
    </div>
  );
};
